import React, { useState, useRef } from 'react';
import { Play, Pause, Volume2, Download, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * AudioAttachment - Audio player component for voice messages
 * Inspired by Rocket.Chat's AudioAttachment
 */
const AudioAttachment = ({
  src,
  filename,
  fileSize,
  mimeType,
  onDownload,
  collapsed: initialCollapsed = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [error, setError] = useState(null);

  const audioRef = useRef(null);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle play/pause
  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Playback failed:', err);
        setError('Failed to play audio');
      });
    }
  };

  // Handle time update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Handle loaded metadata
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Handle seek
  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audioRef.current.currentTime = percentage * duration;
  };

  // Handle play/pause events
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Handle error
  const handleError = () => {
    setError('Failed to load audio');
    setIsPlaying(false);
  };

  // Handle download
  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (src) {
      const link = document.createElement('a');
      link.href = src;
      link.download = filename || 'audio';
      link.click();
    }
  };

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden max-w-sm">
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/50 cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
      >
        <Volume2 className="w-4 h-4 text-slate-500" />
        <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
          {filename || 'Voice message'}
        </span>
        {fileSize && (
          <span className="text-xs text-slate-500">
            {formatFileSize(fileSize)}
          </span>
        )}
        <button
          type="button"
          onClick={handleDownload}
          className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        )}
      </div>

      {/* Player */}
      {!collapsed && (
        <div className="p-3 bg-white dark:bg-slate-900">
          {error ? (
            <div className="text-sm text-red-500 text-center py-2">{error}</div>
          ) : (
            <>
              {/* Hidden audio element */}
              <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                onError={handleError}
              />

              <div className="flex items-center gap-3">
                {/* Play/Pause button */}
                <button
                  type="button"
                  onClick={togglePlay}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-500 text-white hover:bg-yellow-600 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </button>

                {/* Progress bar and time */}
                <div className="flex-1">
                  {/* Progress bar */}
                  <div
                    className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full cursor-pointer overflow-hidden"
                    onClick={handleSeek}
                  >
                    <div
                      className="h-full bg-yellow-500 rounded-full transition-all duration-100"
                      style={{ width: `${progress}%` }}
                    />
                  </div>

                  {/* Time display */}
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-slate-500 font-mono">
                      {formatTime(currentTime)}
                    </span>
                    <span className="text-xs text-slate-500 font-mono">
                      {formatTime(duration)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AudioAttachment;
